import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import type { TenantAttributeSpec } from "@the-real-insight/tri-model";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authenticationAtom } from "../../../State";
import { userAPI } from "../../../UserAPI";
import { buildTree, type DinTree } from "../../../administration/din276/din276Tree";
import AttributesTab from "./AttributesTab";
import ClassCrosswalkTab from "./ClassCrosswalkTab";
import { useClassCrosswalkAPI, type ClassCrosswalkDraft, type ClassCrosswalkEntry } from "./classCrosswalkAPI";
import ClassesTab from "./ClassesTab";
import type { TenantAttribute } from "./dataModel";
import { useDataModelAPI } from "./dataModelAPI";

/**
 * "Datenmodell" of the operator obligations: the DIN 276 classes and the attributes this customer
 * added to them. Three tabs — "Klassen" to find classes and add an attribute to a filter result,
 * "Attribute" to see, change and remove what was added, "Kundenklassen" to map the customer's own
 * asset classes onto DIN 276 (TenantClassCrosswalk).
 *
 * The attributes are stored per tenant on the server (`TenantAttributeDefinition`); the DIN 276
 * itself is shared and not changed here.
 */
export default function DataModelPanel({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useDataModelAPI(authentication?.customer);
  const crosswalkApi = useClassCrosswalkAPI(authentication?.customer);
  const [crosswalk, setCrosswalk] = useState<ClassCrosswalkEntry[]>([]);

  const [categories, setCategories] = useState<any[] | null>(null);
  const [attributes, setAttributes] = useState<TenantAttribute[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("classes");

  const tree: DinTree | null = useMemo(() => (categories ? buildTree(categories) : null), [categories]);

  const loadAttributes = useCallback(async () => {
    setAttributes(await api.getAttributes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  const loadCrosswalk = useCallback(async () => {
    setCrosswalk(await crosswalkApi.getEntries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      userAPI.getDIN276MetadataFull({ normOnly: true }),
      api.getAttributes(),
      // Without the crosswalk the other two tabs still work — an older server simply has none.
      crosswalkApi.getEntries().catch(() => [] as ClassCrosswalkEntry[]),
    ])
      .then(([metadata, loaded, entries]) => {
        if (cancelled) return;
        setCategories(Array.isArray(metadata) ? metadata : []);
        setAttributes(loaded);
        setCrosswalk(entries);
      })
      .catch((loadError: any) => {
        if (!cancelled) setError(loadError?.message ?? t("global.generalError"));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  const create = async (classCodes: string[], spec: TenantAttributeSpec) => {
    const result = await api.createAttribute(classCodes, spec);
    await loadAttributes();
    return result;
  };

  const update = async (ids: string[], spec: TenantAttributeSpec) => {
    await api.updateAttributes(ids, spec);
    await loadAttributes();
  };

  const remove = async (ids: string[]) => {
    const deleted = await api.deleteAttributes(ids);
    await loadAttributes();
    return deleted;
  };

  const createCrosswalk = async (draft: ClassCrosswalkDraft) => {
    await crosswalkApi.createEntry(draft);
    await loadCrosswalk();
  };
  const updateCrosswalk = async (id: string, draft: ClassCrosswalkDraft, confirm: boolean) => {
    await crosswalkApi.updateEntry(id, draft, confirm);
    await loadCrosswalk();
  };
  const confirmCrosswalk = async (ids: string[]) => {
    const count = await crosswalkApi.confirmEntries(ids);
    await loadCrosswalk();
    return count;
  };
  const deleteCrosswalk = async (id: string) => {
    await crosswalkApi.deleteEntry(id);
    await loadCrosswalk();
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="min-w-0 break-words">{error}</AlertDescription>
      </Alert>
    );
  }

  if (!tree) {
    return (
      <div className="flex flex-col gap-2" role="status" aria-label={t("global.loading")}>
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p className="min-w-0 break-words text-sm text-muted-foreground">{t("operatorObligationsPanel.dataModel.intro")}</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="classes">{t("operatorObligationsPanel.dataModel.tabs.classes")}</TabsTrigger>
          <TabsTrigger value="attributes">
            {t("operatorObligationsPanel.dataModel.tabs.attributes")}
            {attributes.length > 0 ? <span className="ml-1.5 text-xs text-muted-foreground">{attributes.length}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="classCrosswalk">
            {t("operatorObligationsPanel.dataModel.tabs.classCrosswalk")}
            {crosswalk.length > 0 ? <span className="ml-1.5 text-xs text-muted-foreground">{crosswalk.length}</span> : null}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="classes" className="mt-4">
          <ClassesTab tree={tree} attributes={attributes} canWrite={canWrite} onCreate={create} />
        </TabsContent>
        <TabsContent value="attributes" className="mt-4">
          <AttributesTab tree={tree} attributes={attributes} canWrite={canWrite} onUpdate={update} onDelete={remove} />
        </TabsContent>
        <TabsContent value="classCrosswalk" className="mt-4">
          <ClassCrosswalkTab
            tree={tree}
            entries={crosswalk}
            canWrite={canWrite}
            onCreate={createCrosswalk}
            onUpdate={updateCrosswalk}
            onConfirm={confirmCrosswalk}
            onDelete={deleteCrosswalk}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
